import {
  IJourney,
  IJourneyMetadata,
  IJourneyStep,
  IJourneyStepAiGenerateConfig,
  IJourneyStepConfigs,
  IJourneyStepMetadata,
  JourneyStepInputType,
  JourneyStepMediaType,
  JourneyStepType,
} from "@model";
import {
  CHARACTER_LANGUAGES,
  type CharacterLanguageKey,
} from "../Character/characterLanguages";

export const JOURNEY_LANGUAGES = CHARACTER_LANGUAGES;

export type JourneyLanguageKey = CharacterLanguageKey;

type LegacyJourneyStep = IJourneyStep & {
  title?: string;
  description?: string;
  starterText?: string;
  mediaContext?: string;
};

type LegacyJourney = Omit<IJourney, "topic"> & {
  description?: string;
  language?: string;
  topic?: IJourney["topic"] | string;
};

export function normalizeJourneyMetadata(
  metadata?: IJourneyMetadata | null,
): IJourneyMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => value && typeof value === "object",
    ),
  );
}

export function cleanJourneyMetadata(
  metadata?: IJourneyMetadata | null,
): IJourneyMetadata {
  const normalized = normalizeJourneyMetadata(metadata);
  return Object.fromEntries(
    Object.entries(normalized)
      .filter(([, value]) => {
        const title = value?.title?.trim();
        const description = value?.description?.trim();
        return Boolean(title || description);
      })
      .map(([key, value]) => [
        key,
        {
          ...(value?.title?.trim() ? { title: value.title.trim() } : {}),
          ...(value?.description?.trim()
            ? { description: value.description.trim() }
            : {}),
        },
      ]),
  );
}

export function normalizeStepMetadata(
  metadata?: IJourneyStepMetadata | null,
): IJourneyStepMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => value && typeof value === "object",
    ),
  );
}

export function readStepAiGenerateConfig(
  step?: Partial<IJourneyStep>,
): IJourneyStepAiGenerateConfig {
  const ai = step?.configs?.aiGenerate;
  return {
    description: ai?.description ?? "",
    stepCustomInstructionHint: ai?.stepCustomInstructionHint ?? "",
    whenBeDoneHint: ai?.whenBeDoneHint ?? "",
    wordCount: typeof ai?.wordCount === "number" ? ai.wordCount : 5,
  };
}

export function cleanStepConfigs(
  configs?: IJourneyStepConfigs | null,
): IJourneyStepConfigs | undefined {
  if (!configs || typeof configs !== "object") return undefined;

  const ai = configs.aiGenerate;
  const cleanAi: IJourneyStepAiGenerateConfig = {
    ...(ai?.description?.trim() ? { description: ai.description.trim() } : {}),
    ...(ai?.stepCustomInstructionHint?.trim()
      ? { stepCustomInstructionHint: ai.stepCustomInstructionHint.trim() }
      : {}),
    ...(ai?.whenBeDoneHint?.trim()
      ? { whenBeDoneHint: ai.whenBeDoneHint.trim() }
      : {}),
    ...(typeof ai?.wordCount === "number" ? { wordCount: ai.wordCount } : {}),
  };

  const otherEntries = Object.entries(configs).filter(
    ([key]) => key !== "aiGenerate",
  );
  const next: IJourneyStepConfigs = {
    ...Object.fromEntries(otherEntries),
    ...(Object.keys(cleanAi).length ? { aiGenerate: cleanAi } : {}),
  };

  return Object.keys(next).length ? next : undefined;
}

export function cleanStepMetadata(
  metadata?: IJourneyStepMetadata | null,
): IJourneyStepMetadata {
  const normalized = normalizeStepMetadata(metadata);
  return Object.fromEntries(
    Object.entries(normalized)
      .filter(([, value]) => {
        const title = value?.title?.trim();
        const description = value?.description?.trim();
        const starterText = value?.starterText?.trim();
        const words =
          value?.words?.filter((entry) => entry?.word?.trim()) ?? [];
        return Boolean(title || description || starterText || words.length);
      })
      .map(([key, value]) => {
        const words =
          value?.words
            ?.map((entry) => ({
              ...(entry?.word?.trim() ? { word: entry.word.trim() } : {}),
            }))
            .filter((entry) => entry.word) ?? [];
        return [
          key,
          {
            ...(value?.title?.trim() ? { title: value.title.trim() } : {}),
            ...(value?.description?.trim()
              ? { description: value.description.trim() }
              : {}),
            ...(value?.starterText?.trim()
              ? { starterText: value.starterText.trim() }
              : {}),
            ...(words.length ? { words } : {}),
          },
        ] as const;
      }),
  );
}

export function getConfiguredJourneyLanguageKeys(
  metadata?: IJourneyMetadata | null,
): string[] {
  return Object.keys(cleanJourneyMetadata(metadata));
}

export function getJourneyLanguageLabels(
  metadata?: IJourneyMetadata | null,
): string[] {
  return getConfiguredJourneyLanguageKeys(metadata).map((key) => {
    const hit = JOURNEY_LANGUAGES.find((lang) => lang.key === key);
    return hit?.label ?? key;
  });
}

export function getStepDisplayTitle(
  step: Partial<IJourneyStep>,
  preferLang = "en",
): string {
  const meta = normalizeStepMetadata(step.metadata);
  const preferred = meta[preferLang]?.title?.trim();
  if (preferred) return preferred;

  for (const lang of JOURNEY_LANGUAGES) {
    const title = meta[lang.key]?.title?.trim();
    if (title) return title;
  }

  return "Untitled step";
}

function migrateLegacyStepFields(step: LegacyJourneyStep): IJourneyStep {
  const metadata = normalizeStepMetadata(step.metadata);
  const legacyTitle = step.title?.trim();
  const legacyDescription = step.description?.trim();
  const legacyStarterText = step.starterText?.trim();

  if (legacyTitle || legacyDescription || legacyStarterText) {
    metadata.en = {
      ...metadata.en,
      ...(legacyTitle ? { title: legacyTitle } : {}),
      ...(legacyDescription ? { description: legacyDescription } : {}),
      ...(legacyStarterText ? { starterText: legacyStarterText } : {}),
    };
  }

  const { title, description, starterText, mediaContext, ...rest } = step;
  return {
    ...rest,
    ...(rest.stepCustomInstruction?.trim()
      ? {}
      : mediaContext?.trim()
        ? { stepCustomInstruction: mediaContext.trim() }
        : {}),
    metadata: Object.keys(metadata).length ? metadata : undefined,
  };
}

export function normalizeJourneyForForm(journey: LegacyJourney): IJourney {
  const metadata = normalizeJourneyMetadata(journey.metadata);
  const legacyDescription = journey.description?.trim();

  if (legacyDescription && !metadata.en?.description) {
    metadata.en = {
      ...metadata.en,
      description: legacyDescription,
    };
  }

  const steps = (journey.steps ?? []).map((step) =>
    migrateLegacyStepFields(step as LegacyJourneyStep),
  );

  const { description, language, topic, ...rest } = journey;
  return {
    ...rest,
    topic: Array.isArray(topic)
      ? topic
      : topic
        ? [topic as IJourney["topic"][number]]
        : [],
    metadata: Object.keys(metadata).length ? metadata : undefined,
    steps: steps.map((step) => ({
      ...step,
      subSteps: step.subSteps?.map((sub) =>
        migrateLegacyStepFields(sub as LegacyJourneyStep),
      ),
    })),
  };
}

const EXTENDED_JSON_NUMBER_KEYS = [
  "$numberInt",
  "$numberLong",
  "$numberDouble",
  "$numberDecimal",
];

/** Collapses Mongo Extended JSON wrappers ({"$oid"}, {"$date"}, {"$numberInt"}). */
function unwrapExtendedJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(unwrapExtendedJson);
  if (!value || typeof value !== "object") return value;

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 1) {
    const [key] = keys;
    if (key === "$oid" || key === "$date") {
      return unwrapExtendedJson(record[key]);
    }
    if (EXTENDED_JSON_NUMBER_KEYS.includes(key)) {
      return Number(record[key]);
    }
  }
  return Object.fromEntries(
    keys.map((key) => [key, unwrapExtendedJson(record[key])]),
  );
}

/** Turns mongo shell helpers (ObjectId("..."), ISODate("..."), NumberInt(1)) into plain JSON. */
function stripMongoShellHelpers(raw: string): string {
  return raw
    .replace(/(?:ObjectId|ISODate)\(\s*("[^"]*")\s*\)/g, "$1")
    .replace(/Number(?:Int|Long|Decimal)\(\s*"?(-?[\d.]+)"?\s*\)/g, "$1");
}

/**
 * Parses a journey pasted from mongo (shell output, Compass/mongoexport JSON,
 * or an admin API response) into form-ready values. Database-only fields are
 * dropped; in create mode step ids are cleared so the copy gets fresh ones.
 */
export function parseJourneyJson(
  raw: string,
  options: { regenerateStepIds: boolean },
): IJourney {
  const parsed = unwrapExtendedJson(JSON.parse(stripMongoShellHelpers(raw)));

  let source: unknown = Array.isArray(parsed) ? parsed[0] : parsed;
  if (
    source &&
    typeof source === "object" &&
    !("steps" in source) &&
    "data" in source
  ) {
    source = (source as { data: unknown }).data;
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    throw new Error("JSON must be a journey object");
  }

  const {
    _id,
    __v,
    createDate,
    isDeleted,
    statistics,
    ...journey
  } = source as Record<string, unknown>;

  const normalized = normalizeJourneyForForm(journey as unknown as IJourney);
  if (!options.regenerateStepIds) return normalized;

  const clearStepId = (step: IJourneyStep): IJourneyStep => ({
    ...step,
    stepId: undefined as unknown as string,
    subSteps: step.subSteps?.map(clearStepId),
  });
  return { ...normalized, steps: normalized.steps?.map(clearStepId) };
}

export function cleanJourneyForSave(values: IJourney): IJourney {
  const sanitizeStepByType = (step: IJourneyStep): IJourneyStep => {
    const {
      stepId,
      type,
      metadata,
      isOptional,
      order,
      xpReward,
      subSteps,
      lessonId,
      wordCardId,
      characterId,
      minDurationMinutes,
      whenBeDone,
      stepCustomInstruction,
      mediaFile,
      mediaType,
      inputType,
      configs,
    } = step;

    const common: IJourneyStep = {
      stepId,
      type,
      metadata: cleanStepMetadata(metadata),
      isOptional,
      order,
      xpReward,
      subSteps: subSteps?.map((sub) => sanitizeStepByType(sub)),
      configs: cleanStepConfigs(configs),
    };

    switch (type) {
      case JourneyStepType.ChatLesson:
        return { ...common, lessonId };
      case JourneyStepType.WordLearning:
        return { ...common, wordCardId };
      case JourneyStepType.LevelTest:
      case JourneyStepType.PartnerTalk:
        return common;
      case JourneyStepType.CharacterTalk:
        return {
          ...common,
          characterId,
          minDurationMinutes,
          ...(whenBeDone?.trim() ? { whenBeDone: whenBeDone.trim() } : {}),
          ...(stepCustomInstruction?.trim()
            ? { stepCustomInstruction: stepCustomInstruction.trim() }
            : {}),
        };
      case JourneyStepType.MediaTalk:
        return {
          ...common,
          characterId,
          ...(whenBeDone?.trim() ? { whenBeDone: whenBeDone.trim() } : {}),
          ...(stepCustomInstruction?.trim()
            ? { stepCustomInstruction: stepCustomInstruction.trim() }
            : {}),
          mediaFile,
          mediaType: mediaType ?? JourneyStepMediaType.Image,
          inputType: inputType ?? JourneyStepInputType.Voice,
        };
      default:
        return common;
    }
  };

  return {
    ...values,
    metadata: cleanJourneyMetadata(values.metadata),
    steps: (values.steps ?? []).map(sanitizeStepByType),
  };
}
